import { Avatar, Stack, Typography } from '@mui/material';
import { ISuppliersResult } from 'API/Suppliers/suppliers';
import { SuppliersContext } from 'Context/SuppliersContext';
import { ITherapySuppliersContext } from 'Context/SuppliersContext/TherapySuppliersContext';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { SUPPLIERS_ID } from 'Enum/suppliers';
import { useDictionary } from 'Hooks';
import { useSupplierContext } from 'Hooks/useSupplierContext';
import { Download2 } from 'Images';
import { onEnterOrEscapeKeyDown } from 'Utils/accessibilityOnKeyDown';
import { sanitizeCsvString } from 'Utils/supplierFilterManager';
import {
  ISupplier,
  getSupplierResource,
  supplierResourceFilterMapper,
} from 'Utils/suppliersResources';
import { useContext } from 'react';

export const Download = () => {
  const { suppliersId, findCity } = useContext(SuppliersContext);
  const genericSuppliers = useSupplierContext();

  const dictionary = useDictionary(Dictionary, DICTIONARY.Suppliers).resultPage;

  const therapyCsvHandler = () => {
    const { resources, suppliers, expertise } =
      genericSuppliers as ITherapySuppliersContext;
    const { filteredResults } = suppliers;

    const csvContent =
      dictionary.therapy.csv.join(',').concat('\n') +
      filteredResults
        .map((result: ISuppliersResult) => {
          const {
            name,
            professions,
            expertises,
            firstStreet,
            firstHouseNumber,
            firstCityId,
            secondStreet,
            secondHouseNumber,
            secondCityId,
            cellPhonePrefix,
            cellPhoneNumber,
            homePhonePrefix,
            homePhoneNumber,
            email,
            hasAccessibilityForHandicapped,
            treatmentMethods,
            languages,
            academicDegreeId,
            instituteName,
          } = result;

          const profession = getSupplierResource(
            resources.OCCUP,
            professions[0]
          );

          const supplierSpecialties = supplierResourceFilterMapper(
            expertise as ISupplier,
            expertises
          ).join(' - ');

          const firstAddress = `${firstStreet || ''} ${
            firstHouseNumber || ''
          } ${findCity(firstCityId)}`;
          const secondAddress = `${secondStreet || ''} ${
            secondHouseNumber || ''
          } ${findCity(secondCityId)}`;

          const firstPhoneNumber = cellPhonePrefix
            ? `0${cellPhonePrefix}-${cellPhoneNumber}`
            : '';
          const secondPhoneNumber = homePhonePrefix
            ? `0${homePhonePrefix}-${homePhoneNumber}`
            : '';

          const supplierEmail = (email || '').toLocaleLowerCase();

          const isAccessible = hasAccessibilityForHandicapped
            ? dictionary.therapy.accessible
            : '';

          const methods = supplierResourceFilterMapper(
            resources.GISHA,
            treatmentMethods
          ).join(' - ');

          const supplierLng = supplierResourceFilterMapper(
            resources.LNGCD,
            languages
          ).join(' - ');

          const degree = getSupplierResource(resources.TOARA, academicDegreeId);

          const institute = instituteName || '';

          return sanitizeCsvString(
            name,
            profession,
            supplierSpecialties,
            firstAddress,
            secondAddress,
            firstPhoneNumber,
            secondPhoneNumber,
            supplierEmail,
            isAccessible,
            methods,
            supplierLng,
            degree,
            institute
          );
        })
        .join('\n');
    return csvContent;
  };

  const doctorsCsvHandler = () => {
    const { resources, suppliers } = genericSuppliers;
    const { filteredResults } = suppliers;

    const csvContent =
      dictionary.doctors.csv.join(',').concat('\n') +
      filteredResults
        .map((result: ISuppliersResult) => {
          const {
            name,
            specialties,
            firstStreet,
            firstHouseNumber,
            firstCityId,
            secondStreet,
            secondHouseNumber,
            secondCityId,
            cellPhonePrefix,
            cellPhoneNumber,
            homePhonePrefix,
            homePhoneNumber,
            email,
            hasAccessibilityForHandicapped,
            languages,
          } = result;

          const profession = getSupplierResource(
            resources.OCCUP,
            specialties[0]
          );

          const firstAddress = `${firstStreet || ''} ${
            firstHouseNumber || ''
          } ${findCity(firstCityId)}`;
          const secondAddress = `${secondStreet || ''} ${
            secondHouseNumber || ''
          } ${findCity(secondCityId)}`;

          const firstPhoneNumber = cellPhonePrefix
            ? `0${cellPhonePrefix}-${cellPhoneNumber}`
            : '';
          const secondPhoneNumber = homePhonePrefix
            ? `0${homePhonePrefix}-${homePhoneNumber}`
            : '';

          const supplierEmail = (email || '').toLocaleLowerCase();

          const isAccessible = hasAccessibilityForHandicapped
            ? dictionary.therapy.accessible
            : '';

          const supplierLng = supplierResourceFilterMapper(
            resources.LNGCD,
            languages
          ).join(' - ');

          return sanitizeCsvString(
            name,
            profession,
            firstAddress,
            secondAddress,
            firstPhoneNumber,
            secondPhoneNumber,
            supplierEmail,
            isAccessible,
            supplierLng
          );
        })
        .join('\n');
    return csvContent;
  };

  const downloadHandler = () => {
    let csvContent = '',
      fileName = '';
    switch (suppliersId) {
      default:
      case SUPPLIERS_ID.mentalTherapy: {
        csvContent = therapyCsvHandler();
        fileName = dictionary.therapy.mentalSuppliersCsvFileName;
        break;
      }
      case SUPPLIERS_ID.doctors: {
        csvContent = doctorsCsvHandler();
        fileName = dictionary.doctors.doctorSupplierCsvFileName;
        break;
      }
    }
    const blob = new Blob([`\ufeff${csvContent}`], {
      type: 'text/csv;charset=utf-8',
    });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', `${fileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Stack
      direction='row'
      alignItems='center'
      gap='7px'
      sx={{
        width: 'fit-content',
        ':hover': {
          cursor: 'pointer',
          textDecoration: 'underline',
        },
      }}
      tabIndex={0}
      component='a'
      onClick={downloadHandler}
      onKeyDown={onEnterOrEscapeKeyDown}
    >
      <Typography
        variant='body3'
        sx={{ color: 'primary.main', whiteSpace: 'nowrap' }}
      >
        {Dictionary[DICTIONARY.Suppliers].resultPage.downloadAsFile}
      </Typography>
      <Avatar alt='' src={Download2} sx={{ width: '24px', height: '24px' }} />
    </Stack>
  );
};
